import { useMutation } from '@apollo/client';
import { datadogRum } from '@datadog/browser-rum';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { loadConversation, loadConversations, saveMessage } from 'lib/buddyChatHistory';
import { DEV_USER_ID, clearSeedData } from 'lib/buddyChatHistory/seed';
import { CHAT_MUTATION } from 'pages/Assistant/gql/chat';
import { formatMsgTime } from 'utils/formatMsgTime';

import { buddyStore } from './BuddyStore';
import { DRAG_MOVE_THRESHOLD } from './const';

export function useBuddy() {
	const navigate = useNavigate();
	const dragState = useRef({
		isDragging: false,
		startClientX: 0,
		startClientY: 0,
		offsetX: 0,
		offsetY: 0,
		moved: false,
	});
	const [isDragging, setIsDragging] = useState(false);
	const inputRef = useRef(null);
	const messagesEndRef = useRef(null);
	const [inputValue, setInputValue] = useState('');
	const activeConvIdRef = useRef(null);

	const { isOpen, position, messages, isLoading } = buddyStore;

	const [sendChat] = useMutation(CHAT_MUTATION);

	// position is stored as right/bottom CSS offsets. Capture the cursor's
	// distance from the button's right and bottom edges so pointer-move can
	// produce right/bottom values directly rather than left/top coordinates.
	const handlePointerDown = useCallback(
		(e) => {
			dragState.current = {
				isDragging: true,
				startClientX: e.clientX,
				startClientY: e.clientY,
				offsetX: window.innerWidth - e.clientX - position.x,
				offsetY: window.innerHeight - e.clientY - position.y,
				moved: false,
			};
			setIsDragging(true);

			e.preventDefault();
		},
		[position.x, position.y]
	);

	const handlePointerMove = useCallback((e) => {
		if (!dragState.current.isDragging) return;

		const dx = Math.abs(e.clientX - dragState.current.startClientX);
		const dy = Math.abs(e.clientY - dragState.current.startClientY);

		if (dx > DRAG_MOVE_THRESHOLD || dy > DRAG_MOVE_THRESHOLD) {
			dragState.current.moved = true;
		}

		const newX = window.innerWidth - e.clientX - dragState.current.offsetX;
		const newY = window.innerHeight - e.clientY - dragState.current.offsetY;
		const margin = 8;
		const clampedX = Math.max(margin, Math.min(window.innerWidth - 64 - margin, newX));
		const clampedY = Math.max(margin, Math.min(window.innerHeight - 64 - margin, newY));

		buddyStore.setPosition({ x: clampedX, y: clampedY });
	}, []);

	const handlePointerUp = useCallback(() => {
		if (!dragState.current.isDragging) return;

		const { moved } = dragState.current;
		dragState.current.isDragging = false;

		setIsDragging(false);

		if (!moved) buddyStore.toggleOpen();
	}, []);

	useEffect(() => {
		window.addEventListener('pointermove', handlePointerMove);
		window.addEventListener('pointerup', handlePointerUp);

		return () => {
			window.removeEventListener('pointermove', handlePointerMove);
			window.removeEventListener('pointerup', handlePointerUp);
		};
	}, [handlePointerMove, handlePointerUp]);

	const loadHistory = useCallback(async () => {
		await clearSeedData();

		const convs = await loadConversations({ userId: DEV_USER_ID });

		if (convs.length === 0) return;

		const latest = convs[0];
		activeConvIdRef.current = latest.conversationId;

		const msgs = await loadConversation({
			userId: DEV_USER_ID,
			conversationId: latest.conversationId,
		});

		buddyStore.setMessages(
			msgs.map((m) => ({
				id: String(m.id),
				role: m.role,
				text: m.text,
				card: m.card ?? null,
				time: formatMsgTime(m.createdAt),
			}))
		);
	}, []);

	useEffect(() => {
		loadHistory().catch((err) =>
			datadogRum.addError(err, { source: 'buddy', operation: 'loadHistory' })
		);
	}, [loadHistory]);

	useEffect(() => {
		messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
	}, [messages.length, isLoading]);

	useEffect(() => {
		if (isOpen) setTimeout(() => inputRef.current?.focus(), 80);
	}, [isOpen]);

	const handleExpandToFullView = () => {
		buddyStore.setOpen(false);

		navigate('/assistant');
	};

	const handleClose = () => buddyStore.setOpen(false);

	const handleInputChange = (e) => setInputValue(e.target.value);

	const handleSend = async (overrideText) => {
		const text = (overrideText !== undefined ? overrideText : inputValue).trim();

		if (!text) return;
		if (overrideText === undefined) setInputValue('');

		const convId = activeConvIdRef.current ?? `conv-${Date.now()}`;

		if (!activeConvIdRef.current) activeConvIdRef.current = convId;

		const history = buddyStore.messages
			.filter((m) => m.id !== 'greeting')
			.map((m) => ({ role: m.role.toUpperCase(), text: m.text }));

		history.push({ role: 'USER', text });
		buddyStore.addUserMessage(text);
		buddyStore.setLoading(true);

		const userCreatedAt = Date.now();

		await saveMessage({
			userId: DEV_USER_ID,
			conversationId: convId,
			role: 'user',
			text,
			card: null,
			createdAt: userCreatedAt,
		});

		try {
			const { data } = await sendChat({ variables: { input: { messages: history } } });
			const response = data.chat;
			const card =
				response.crewMembers?.length > 0
					? { type: 'crewMembers', data: response.crewMembers }
					: null;

			buddyStore.addAssistantMessage(response.text, card);
			buddyStore.setLoading(false);

			await saveMessage({
				userId: DEV_USER_ID,
				conversationId: convId,
				role: 'assistant',
				text: response.text,
				card,
				createdAt: Date.now(),
			});
		} catch {
			buddyStore.setLoading(false);
		}
	};

	const handleInputKeyDown = (e) => {
		if (e.key === 'Enter' && !e.shiftKey) {
			e.preventDefault();
			handleSend();
		}
	};

	const handleEditAndAsk = (text) => {
		setInputValue(text);
		inputRef.current?.focus?.();
	};

	const handleResend = (text) => handleSend(text);

	return {
		isDragging,
		isOpen,
		position,
		messages,
		isLoading,
		inputValue,
		inputRef,
		messagesEndRef,
		handlePointerDown,
		handleExpandToFullView,
		handleClose,
		handleInputChange,
		handleSend,
		handleInputKeyDown,
		handleEditAndAsk,
		handleResend,
	};
}
